'use client';
import { PurchaseGameByTokenButton } from '@/features/membership/shared/components/purchase-game-by-token-button';
import { GameData } from '@/features/games/shared/types/game-data';
import { WishlistDropdown } from '@/features/games/shared/components/wishlist-dropdown';
import { ShareDropdown } from '@/features/games/shared/components/share-dropdown';
import { useTranslations } from 'next-intl';
import { usePurchaseGate } from '@/features/games/shared/hooks/use-purchase-gate';
import { PurchaseGameByTokenOverlay } from '@/features/membership/shared/components/purchase-game-by-token-overlay';
import Modal from '@/shared/components/overlay/modal';
import { PurchaseGamesOptions } from '@/features/games/internal/components/game-detail/download-game/purchase-games-options';
import { useAppStore } from '@/features/shared/app-store';
import { User } from 'next-auth';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { useEffect, useState } from 'react';

export const PurchaseDetails = ({ gameData, user }: { gameData: GameData; user: User | null }) => {
  const t = useTranslations('GamePage');
  const { isReady, isPurchased } = usePurchaseGate(gameData.sku);
  const cartInitialized = useAppStore((s) => s.cartInitialized);
  const fetchPrices = useGameStore((s) => s.fetchPrices);
  const [resolvedPriceSku, setResolvedPriceSku] = useState<string | null>(null);
  const isPricesLoading = resolvedPriceSku !== gameData.sku;

  useEffect(() => {
    let isActive = true;

    fetchPrices(gameData.sku)
      .catch(() => {})
      .finally(() => {
        if (isActive) {
          setResolvedPriceSku(gameData.sku);
        }
      });

    return () => {
      isActive = false;
    };
  }, [gameData.sku, fetchPrices]);

  let purchaseSection: React.ReactNode = null;

  if (!isReady || !cartInitialized || isPricesLoading) {
    purchaseSection = (
      <div className='bg-background-panel h-[294px] w-full animate-pulse rounded-lg' />
    );
  } else if (isPurchased) {
    purchaseSection = <div className='flex h-[294px]' />;
  } else {
    purchaseSection = (
      <>
        <div role='separator' aria-label='or' className='mb-2 flex items-center gap-4 px-8'>
          <div className='bg-background-subtle h-0.5 flex-1' aria-hidden='true' />
          <span className='text-base tracking-wider select-none'>{t('or')}</span>
          <div className='bg-background-subtle h-0.5 flex-1' aria-hidden='true' />
        </div>

        <PurchaseGamesOptions gameData={gameData} />
        {user && user?.isWildMember && <PurchaseGameByTokenButton gameData={gameData} />}
        <div className='mt-8 flex justify-center gap-4'>
          {user && <WishlistDropdown gameData={gameData} />}
          <ShareDropdown />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Always-mounted modal host; opened via store (showOverlay('purchase-game-by-token')) */}
      <Modal modalKey='purchase-game-by-token'>
        <PurchaseGameByTokenOverlay gameData={gameData} />
      </Modal>
      {purchaseSection}
    </>
  );
};
